import { useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { useServerTick } from "../ui/Timer.js";
import { CardArt, OfferCard, Price } from "./cards.js";
import type { StoreCard } from "./types.js";

/** Keep the selected offer stable through refreshes; expired offers leave the selection. */
export function selectableOffers(cards: StoreCard[], now: number): StoreCard[] {
  return [...new Map(cards.map(card => [card.key, card])).values()]
    .filter(card => card.expiresAt === null || card.expiresAt > now)
    .sort((a, b) => Number(a.locked !== null || !!a.limit && a.limit.used >= a.limit.max) - Number(b.locked !== null || !!b.limit && b.limit.used >= b.limit.max));
}

/** `preferKey`: the card shown first while it is still available (the once-only Starter Pack leads the packs). */
export function OfferShowcase({ cards, title, testId, preferKey }: { cards: StoreCard[]; title: string; testId: string; preferKey?: string }) {
  const t = useT(), now = useServerTick();
  const [selected, setSelected] = useState<string | null>(null);
  const choices = selectableOffers(cards, now);
  const preferred = preferKey ? choices.find(card => card.key === preferKey && card.locked === null && !(card.limit && card.limit.used >= card.limit.max)) : undefined;
  const active = choices.find(card => card.key === selected) ?? preferred ?? choices[0];
  return <section className="offer-showcase" data-testid={testId}>
    <header className="offer-showcase-heading"><h1>{title}</h1><span>{choices.length} {pick({ de: "Angebote", en: "offers" })}</span></header>
    {active ? <OfferCard key={active.key} card={active} /> : <p className="hint">{pick({ de: "Zurzeit kein aktives Angebot.", en: "No active offer right now." })}</p>}
    {choices.length > 1 ? <div className="offer-choice-grid" aria-label={t("store.redesign.choose")}>
      {choices.map(card => <button type="button" key={card.key} className={`offer-choice ${card.key === active?.key ? "selected" : ""}`} aria-pressed={card.key === active?.key} onClick={() => setSelected(card.key)} data-testid={`select-${card.key}`}>
        <CardArt art={card.art} size={54} /><span className="offer-choice-copy"><strong>{pick(card.name)}</strong><Price price={card.price} currency={card.currency} /></span>
        {card.double ? <span className="offer-choice-bonus">2×</span> : null}
      </button>)}
    </div> : null}
  </section>;
}
